import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  Org,
  seatPeriodAmount,
  seatPriceBreakdown,
  type Auth,
  type SeatPriceBreakdownRow,
} from "tf-api-client";
import api from "../api";
import i18nUtil from "../utils/i18n";

// Seat tiers of both payment options and the trial terms of one organization
// (`GET /org/:id/billing/price`, any token of that organization, including an
// onboarding one; not public). Fetched once per organization and page load,
// and shared: the card step and the upgrade dialog both show it. A failure
// (503 when billing isn't configured) leaves `price` null; callers then hide
// the price, never invent one.
type Entry = {
  price: Ref<Auth.SeatPrice | null>;
  failed: Ref<boolean>;
  pending: Promise<void> | null;
};
const entries = new Map<string, Entry>();

const entryFor = (orgId: string): Entry => {
  let entry = entries.get(orgId);
  if (!entry) {
    entry = { price: ref(null), failed: ref(false), pending: null };
    entries.set(orgId, entry);
  }
  return entry;
};

const load = (orgId: string): Promise<void> => {
  const entry = entryFor(orgId);
  if (entry.price.value) return Promise.resolve();
  if (!entry.pending) {
    entry.failed.value = false;
    entry.pending = api.org
      .getSeatPrice(orgId)
      .then((result) => {
        entry.price.value = result;
      })
      .catch((err) => {
        console.error("Failed to load the seat price", err);
        entry.failed.value = true;
      })
      .finally(() => {
        entry.pending = null;
      });
  }
  return entry.pending;
};

// Amounts are in the currency's smallest unit: scale by the currency's own
// number of decimals (2 for EUR, 0 for JPY) rather than assuming cents.
const minorUnitDigits = (currency: string): number => {
  try {
    return (
      new Intl.NumberFormat("en", { style: "currency", currency })
        .resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
};

export const formatMinorAmount = (amount: number, currency: string): string => {
  const code = currency.toUpperCase();
  return i18nUtil.formatCurrency(amount / 10 ** minorUnitDigits(code), code);
};

// Without an organization (public pages) there is no price.
export function useSeatPrice(
  orgId: MaybeRefOrGetter<string | null | undefined>,
) {
  const { t, te } = useI18n({ useScope: "global" });

  const entry = computed(() => {
    const id = toValue(orgId);
    return id ? entryFor(id) : null;
  });
  const price = computed(() => entry.value?.price.value ?? null);
  const failed = computed(() => entry.value?.failed.value ?? false);

  watch(
    () => toValue(orgId),
    (id) => {
      if (id) load(id);
    },
    { immediate: true },
  );
  const reload = () => {
    const id = toValue(orgId);
    return id ? load(id) : Promise.resolve();
  };

  // the tiers of a payment option
  const listFor = (
    option: Org.SubscriptionBillingInterval,
  ): Auth.SeatPriceList | null => {
    if (!price.value) return null;
    return option === Org.SubscriptionBillingInterval.Annual
      ? price.value.annual
      : price.value.monthly;
  };

  // "month" -> "month" in the UI language; an interval the backend adds later
  // is shown as sent rather than hidden
  const intervalLabel = (option: Org.SubscriptionBillingInterval): string => {
    const interval = listFor(option)?.interval;
    if (!interval) return "";
    const key = `licensing.price.interval.${interval}`;
    return te(key) ? t(key) : interval;
  };

  const format = (amount: number): string =>
    price.value ? formatMinorAmount(amount, price.value.currency) : "";

  // The tiers are cumulative: each prices only the seats in its range.
  const breakdown = (
    option: Org.SubscriptionBillingInterval,
    seats: number,
  ): SeatPriceBreakdownRow[] => {
    const list = listFor(option);
    return list ? seatPriceBreakdown(list, seats) : [];
  };

  // what one period (a month or a year) costs for `seats`, in the currency
  const periodTotal = (
    option: Org.SubscriptionBillingInterval,
    seats: number,
  ): string => {
    const list = listFor(option);
    return list ? format(seatPeriodAmount(list, seats)) : "";
  };

  // VAT is added at checkout unless the prices include it
  const taxIncluded = computed(() => price.value?.taxBehavior === "inclusive");

  return {
    price,
    failed,
    reload,
    listFor,
    intervalLabel,
    format,
    breakdown,
    periodTotal,
    taxIncluded,
  };
}
